import { Link, useLocation } from 'react-router-dom';
import './index.scss';
import { Layout as BaseLayout, Typography, Tooltip, Badge } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { SvgIcon } from 'src/components/icon';
import { ROUTES } from 'src/types/routes';
import { useState, useMemo } from 'react';
import { selectUser } from '@/slice/user';
import { useAppSelector } from '@/hooks/useAppSelector';
import { getStoredRole } from '@/hooks/useAuthValidation';
import { getFreeListingStats } from '@/api/freeListing';

type MenuItem = {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  subPaths?: string[];
  hidden?: boolean;
  // TCX-666: only shown when the signed-in JWT role is 'superAdmin'.
  superAdminOnly?: boolean;
  // TCX-903: when set, the item renders a count badge fed by that query.
  // Only Free Listing uses it today; the field keeps the badge out of the
  // render loop's unconditional path.
  badge?: 'freeListingPending';
};

const menuItems: MenuItem[] = [
  {
    key: ROUTES.ADMIN_DASHBOARD,
    label: 'Dashboard',
    path: ROUTES.ADMIN_DASHBOARD,
    icon: (
      <SvgIcon
        type="dashboard"
        width={24}
        height={24}
        style={{ fill: 'white' }}
      />
    ),
    subPaths: [ROUTES.ADMIN_DASHBOARD],
  },
  {
    key: ROUTES.ADMIN_CONSULTANTS,
    label: 'Consultants',
    path: ROUTES.ADMIN_CONSULTANTS,
    icon: <SvgIcon type="consultant" width={24} height={24} />,
    subPaths: [
      ROUTES.ADMIN_CONSULTANTS,
      ROUTES.ADMIN_CONSULTANTS_DETAILED_PAGE,
      ROUTES.ADMIN_CONSULTANTS_CREATE_DETAILS,
      ROUTES.ADMIN_CONSULTANTS_EDIT_DETAILS,
    ],
  },
  {
    key: ROUTES.ADMIN_FRANCHISES,
    label: 'Franchisors',
    path: ROUTES.ADMIN_FRANCHISES,
    icon: <SvgIcon type="franchise" width={24} height={24} />,
    subPaths: [
      ROUTES.ADMIN_FRANCHISES,
      ROUTES.ADMIN_FRANCHISES_DETAILED_PAGE,
      ROUTES.ADMIN_FRANCHISES_CREATE_DETAILS,
      ROUTES.ADMIN_FRANCHISES_EDIT_DETAILS,
    ],
  },
  {
    key: ROUTES.ADMIN_BOOKINGS,
    label: 'Bookings',
    path: ROUTES.ADMIN_BOOKINGS,
    icon: <SvgIcon type="calendar" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_BOOKINGS, ROUTES.ADMIN_BOOKINGS_DETAILED_PAGE],
  },
  {
    key: ROUTES.ADMIN_PAYMENTS,
    label: 'Payments',
    path: ROUTES.ADMIN_PAYMENTS,
    icon: <SvgIcon type="payments" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_PAYMENTS],
  },
  {
    key: ROUTES.ADMIN_PROMOTIONS,
    label: 'Promotions',
    path: ROUTES.ADMIN_PROMOTIONS,
    icon: <SvgIcon type="promotion" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_PROMOTIONS],
    hidden: true,
  },
  {
    key: ROUTES.ADMIN_BRAND_NAMES,
    label: 'Company Brands',
    path: ROUTES.ADMIN_BRAND_NAMES,
    icon: <SvgIcon type="brand" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_BRAND_NAMES],
    hidden: true,
  },
  {
    key: ROUTES.ADMIN_REVIEWS,
    label: 'Reviews',
    path: ROUTES.ADMIN_REVIEWS,
    icon: <SvgIcon type="star" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_REVIEWS],
  },
  {
    key: ROUTES.ADMIN_CONSULTANT_REFERENCE,
    label: 'Reference',
    path: ROUTES.ADMIN_CONSULTANT_REFERENCE,
    icon: <SvgIcon type="link" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_CONSULTANT_REFERENCE],
  },
  {
    key: ROUTES.ADMIN_BRAND_MAPPING_MANAGEMENT,
    label: 'Brand Mapping',
    path: ROUTES.ADMIN_BRAND_MAPPING_MANAGEMENT,
    icon: <SvgIcon type="brandMapping" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_BRAND_MAPPING_MANAGEMENT],
  },
  {
    key: ROUTES.ADMIN_EMAIL_MANAGEMENT,
    label: 'Email Management',
    path: ROUTES.ADMIN_EMAIL_MANAGEMENT,
    icon: <SvgIcon type="comments" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_EMAIL_MANAGEMENT],
  },
  {
    key: ROUTES.ADMIN_FREE_LISTING,
    label: 'Free Listing',
    path: ROUTES.ADMIN_FREE_LISTING,
    icon: <SvgIcon type="chat" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_FREE_LISTING],
    badge: 'freeListingPending',
  },
  {
    key: ROUTES.ADMIN_LEGAL_DOCUMENTS,
    label: 'Legal Documents',
    path: ROUTES.ADMIN_LEGAL_DOCUMENTS,
    icon: <SvgIcon type="upload" width={24} height={24} />,
    subPaths: [
      ROUTES.ADMIN_LEGAL_DOCUMENTS,
      ROUTES.ADMIN_LEGAL_DOCUMENTS_EDIT,
    ],
  },
  {
    key: ROUTES.ADMIN_SETTINGS,
    label: 'Settings',
    path: ROUTES.ADMIN_SETTINGS,
    icon: <SvgIcon type="settingsNew" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_SETTINGS],
  },
  {
    key: ROUTES.ADMIN_API_KEYS,
    label: 'API Keys',
    path: ROUTES.ADMIN_API_KEYS,
    icon: <SvgIcon type="lock" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_API_KEYS],
  },
  {
    key: ROUTES.ADMIN_POPUP,
    label: 'Pop-up',
    path: ROUTES.ADMIN_POPUP,
    icon: <SvgIcon type="popup" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_POPUP],
  },
  {
    key: ROUTES.ADMIN_ADMINISTRATORS,
    label: 'Administrators',
    path: ROUTES.ADMIN_ADMINISTRATORS,
    icon: <SvgIcon type="shield" width={24} height={24} />,
    subPaths: [ROUTES.ADMIN_ADMINISTRATORS],
    superAdminOnly: true,
  },
];

export default function Sidebar() {
  const user = useAppSelector(selectUser);
  const { pathname } = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(true);

  // TCX-666: super-admins are scoped to admin-management screens only — the
  // other admin screens are @Roles(ADMIN)-only on the BE and would 403, so a
  // super-admin sees ONLY superAdminOnly items; everyone else sees the rest.
  const role = getStoredRole();
  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter((item) => {
        if (item.hidden) return false;
        return role === 'superAdmin'
          ? !!item.superAdminOnly
          : !item.superAdminOnly;
      }),
    [role],
  );

  // TCX-903 AC: the chat icon carries a red badge with the pending count, and
  // it "updates on response" — the drawer invalidates this key after a
  // successful answer, so no polling and no socket is needed.
  const { data: freeListingStats } = useQuery({
    queryKey: ['free-listing-stats'],
    queryFn: getFreeListingStats,
  });

  // Match active route (handles dynamic paths like /:id)
  const activeKey = useMemo(() => {
    return visibleMenuItems.find(({ subPaths }) =>
      subPaths?.some((route) => {
        const pattern = route.replace(/:\w+/g, '[^/]+'); // handle /:id
        return new RegExp(`^${pattern}$`).test(pathname);
      }),
    )?.key;
  }, [pathname, visibleMenuItems]);

  return (
    <BaseLayout.Sider
      className="admin-sidebar"
      collapsible
      collapsed={isCollapsed}
      onCollapse={setIsCollapsed}
      trigger={null}
      width={250}
    >
      <div>
        <div className="admin-sidebar-logo-wrapper">
          <div className="admin-sidebar-logo">
            <SvgIcon type="logoAdmin" width={20} height={20} />
          </div>
          <Typography.Text className="admin-logo-naming">TCX</Typography.Text>
        </div>

        <nav className="admin-sidebar-top">
          {visibleMenuItems.map(({ key, path, icon, label, badge }) => {
            const badgeCount =
              badge === 'freeListingPending'
                ? (freeListingStats?.pendingRequests ?? 0)
                : 0;

            return (
              <Link
                key={key}
                to={path}
                className={`admin-sidebar-link admin-sidebar-item collapsed ${
                  activeKey === key ? 'active' : ''
                }`}
              >
                <span style={{ width: 24, height: 24, position: 'relative' }}>
                  {icon}
                  {badgeCount > 0 && (
                    <Badge
                      count={badgeCount}
                      size="small"
                      style={{ position: 'absolute', top: -4, right: -8 }}
                    />
                  )}
                </span>
                <span className="admin-sidebar-link-label">{label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="admin-sidebar-bottom">
        <div
          className="admin-sidebar-link admin-sidebar-item collapsed"
          style={{ paddingLeft: 18 }}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <span style={{ width: 24, height: 24 }}>
            <SvgIcon
              type="logOut"
              width={24}
              height={24}
              className={
                isCollapsed
                  ? 'collapsable-link-closed'
                  : 'collapsable-link-open'
              }
            />
          </span>
          {!isCollapsed && (
            <span className="admin-sidebar-link-label">Minimize Menu</span>
          )}
        </div>

        <div
          className={`admin-profile-link ${
            !isCollapsed ? 'collapsed-profile' : ''
          }`}
          style={{ paddingLeft: 10 }}
        >
          <div
            style={{
              height: 40,
              width: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Link
              to={ROUTES.ADMIN_PROFILE}
              className="admin-sidebar-link admin-sidebar-profile"
            >
              <SvgIcon type="profile" />
            </Link>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              overflow: 'hidden',
            }}
          >
            {!isCollapsed && (
              <>
                <Typography.Text className="admin-sidebar-profile-label">
                  {user.firstName} {user.lastName}
                </Typography.Text>
                <Tooltip placement="right" title={user.email}>
                  <Typography.Text className="admin-sidebar-profile-label email">
                    {user.email}
                  </Typography.Text>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </div>
    </BaseLayout.Sider>
  );
}
